import { Alert, Button, Card, Form, Input, Space, message } from 'antd';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import LmsStatusTag from '@/pages/education/components/LmsStatusTag';
import { EducationSectionSelect } from '@/pages/education/components/EducationLookupSelects';
import { lmsErrorMessage, useLmsAccess } from '@/pages/education/utils/lmsAccess';

type Announcement = { id: string; section_id: string; title: string; body_text?: string | null; status: string };
type Values = { section_id: string; title: string; body_text?: string };

const LIST_PATH = '/education/lms/announcements';

export default function ClassAnnouncementFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canManage } = useLmsAccess();
  const [form] = Form.useForm<Values>();

  const detail = useQuery({
    queryKey: ['education-lms-announcement', id],
    enabled: !isNew,
    queryFn: async () => (await api.get<Announcement>(`/education/lms/announcements/${id}`)).data,
  });
  const announcement = detail.data;
  useEffect(() => {
    if (!announcement) return;
    form.setFieldsValue({
      section_id: announcement.section_id,
      title: announcement.title,
      body_text: announcement.body_text || undefined,
    });
  }, [announcement, form]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['education-lms-announcements'] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-announcement', id] });
    queryClient.invalidateQueries({ queryKey: ['education-lms-summary'] });
  };
  const save = useMutation({
    mutationFn: (values: Values) => {
      const payload = { ...values, body_text: values.body_text?.trim() || null };
      return isNew
        ? api.post('/education/lms/announcements', payload)
        : api.put(`/education/lms/announcements/${id}`, { title: payload.title, body_text: payload.body_text });
    },
    onSuccess: () => { message.success(t('lmsSaved', { defaultValue: 'Saved' })); refresh(); navigate(LIST_PATH); },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to save announcement')),
  });
  const publish = useMutation({
    mutationFn: () => api.post(`/education/lms/announcements/${id}/publish`),
    onSuccess: () => { message.success(t('lmsPublishedOk', { defaultValue: 'Published' })); refresh(); navigate(LIST_PATH); },
    onError: (e) => message.error(lmsErrorMessage(e, 'Unable to publish announcement')),
  });

  return (
    <EducationPageChrome
      title={isNew ? t('lmsNewAnnouncement', { defaultValue: 'New announcement' }) : t('lmsEditAnnouncement', { defaultValue: 'Edit announcement' })}
      tags={announcement ? <LmsStatusTag status={announcement.status} /> : undefined}
      extra={<Button onClick={() => navigate(LIST_PATH)}>{t('lmsAnnouncements', { defaultValue: 'Class announcements' })}</Button>}
    >
      {!canManage ? <Alert type="warning" showIcon message={t('lmsReadOnly', { defaultValue: 'Read-only access.' })} /> : null}
      <Card size="small" style={{ maxWidth: 820 }} loading={!isNew && detail.isLoading}>
        <Form<Values> form={form} layout="vertical" disabled={!canManage} onFinish={(v) => save.mutate(v)}>
          <Form.Item name="section_id" label={t('section', { defaultValue: 'Section' })} rules={[{ required: true }]}>
            <EducationSectionSelect disabled={!isNew} />
          </Form.Item>
          <Form.Item name="title" label={t('title', { defaultValue: 'Title' })} rules={[{ required: true, max: 250 }]}>
            <Input maxLength={250} />
          </Form.Item>
          <Form.Item name="body_text" label={t('lmsBodyText', { defaultValue: 'Content' })}>
            <Input.TextArea rows={8} maxLength={20000} showCount />
          </Form.Item>
          {canManage ? (
            <Space wrap>
              <Button type="primary" htmlType="submit" loading={save.isPending}>{t('save', { defaultValue: 'Save' })}</Button>
              {!isNew && announcement?.status === 'draft' ? (
                <Button onClick={() => publish.mutate()} loading={publish.isPending}>{t('lmsPublish', { defaultValue: 'Publish' })}</Button>
              ) : null}
              <Button onClick={() => navigate(LIST_PATH)}>{t('cancel', { defaultValue: 'Cancel' })}</Button>
            </Space>
          ) : null}
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
